// Live data for My Time Entry: the signed-in employee, the projects they can
// log to, per-project activity lists (loaded on demand) and the conversion of
// BQE time entries into grid rows, shared with every view through context.
// Also the pure helpers for what's changed, sending and drafts.

// DEPENDENCIES
import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from "react";

// API IMPORTS
import { getAPICall } from "../../config/apiCalls";

// HELPERS
import { emptyCell, signInExpired } from "./timeEntryUtils";

export const TimeEntryDataContext = createContext(null);
export const useTimeEntryData = () => useContext(TimeEntryDataContext);

const RECENT_LIMIT = 8;
const BASICS_STALE_MS = 60 * 60 * 1000; // project list: refresh after an hour

// Groups one week's BQE entries into grid rows: one per project + activity,
// with a cell per day. A cell holding several BQE entries shows their total.
export function rowsFromEntries(entries, week, firstId) {
	let nextId = firstId;
	const byKey = new Map();
	entries.forEach((e) => {
		const key = e.projectId + "|" + e.activityId;
		if (!byKey.has(key)) {
			byKey.set(key, {
				id: nextId++,
				projectId: e.projectId,
				activityId: e.activityId,
				days: [],
				cells: week.map(() => ({
					...emptyCell(e.billable),
					entries: [],
				})),
			});
		}
		const day = week.findIndex((d) => d.iso === e.date);
		if (day >= 0) byKey.get(key).cells[day].entries.push(e);
	});
	const rows = [...byKey.values()];
	rows.forEach((row) => {
		row.cells = row.cells.map((c) => {
			if (!c.entries.length) return c;
			const first = c.entries[0];
			const hours = c.entries.reduce((a, e) => a + e.hours, 0);
			return {
				...c,
				hours: String(Math.round(hours * 100) / 100),
				memo: c.entries
					.map((e) => e.memo)
					.filter(Boolean)
					.join("; "),
				billable: first.billable,
				overtime: first.overtime,
				extraTime: first.extraTime,
				compTime: first.compTime,
				lockReason:
					c.entries.map((e) => e.lockReason).find(Boolean) || null,
			};
		});
	});
	return { rows, nextId };
}

// ── Posting ──────────────────────────────────────────────────────────────────

const round1 = (n) => Math.round(n * 10) / 10;
const toNum = (v) => {
	const n = parseFloat(v);
	return Number.isFinite(n) ? n : 0;
};
const groupKey = (projectId, activityId, date) =>
	projectId + "|" + activityId + "|" + date;

// BQE entries grouped the way the grid shows them: project + activity + day
const groupEntries = (entries) => {
	const map = new Map();
	entries.forEach((e) => {
		const k = groupKey(e.projectId, e.activityId, e.date);
		map.set(k, [...(map.get(k) || []), e]);
	});
	return map;
};

// A day's BQE entries as one cell's values (same rules as rowsFromEntries)
const baseValues = (group) => ({
	hours: round1(group.reduce((a, e) => a + e.hours, 0)),
	memo: group
		.map((e) => e.memo)
		.filter(Boolean)
		.join("; ")
		.trim(),
	billable: !!group[0].billable,
	compTime: !!group[0].compTime,
});

/**
 * The cells that differ from what was loaded from BQE, ready to post.
 * Lines removed from the grid come back as 0 hours so their entries get
 * deleted. Locked entries are never included.
 * @param {Array} rows - the week's grid rows
 * @param {Array} baseline - the week's entries as loaded from BQE
 * @param {Array} week - the week's days ({ iso })
 * @param {string} onlyDate - optional YYYY-MM-DD to post just one day (mobile)
 */
export function changedCells(rows, baseline, week, onlyDate) {
	const base = groupEntries(baseline);
	const seen = new Set();
	const out = [];
	const consider = (projectId, activityId, date, cell) => {
		if (onlyDate && date !== onlyDate) return;
		const k = groupKey(projectId, activityId, date);
		seen.add(k);
		const group = base.get(k) || [];
		if (group.some((e) => e.lockReason) || (cell && cell.lockReason))
			return;
		const want = {
			hours: cell ? round1(toNum(cell.hours)) : 0,
			memo: cell ? cell.memo.trim() : "",
			billable: cell ? !!cell.billable : false,
			compTime: cell ? !!cell.compTime : false,
		};
		if (!group.length) {
			if (want.hours <= 0) return;
		} else {
			const was = baseValues(group);
			const same =
				want.hours === was.hours &&
				(want.hours <= 0 ||
					(want.memo === was.memo &&
						want.billable === was.billable &&
						want.compTime === was.compTime));
			if (same) return;
		}
		out.push({
			projectId,
			activityId,
			date,
			...want,
			baseEntries: group.map((e) => ({ id: e.id, version: e.version })),
		});
	};
	rows.forEach((r) =>
		r.cells.forEach((c, i) =>
			consider(r.projectId, r.activityId, week[i].iso, c),
		),
	);
	// Loaded entries whose line was removed from the grid
	base.forEach((group, k) => {
		if (seen.has(k)) return;
		const e = group[0];
		consider(e.projectId, e.activityId, e.date, null);
	});
	return out;
}

const sameGroup = (a = [], b = []) =>
	a.length === b.length &&
	a.every((e) => b.some((x) => x.id === e.id && x.version === e.version));

/**
 * Sorts unsent edits (after a refresh, or from a saved draft) against fresh
 * BQE data: an edit is kept unless BQE changed or locked that same day and
 * activity since the edit was made, in which case BQE wins.
 * @param {Array} edits - changedCells() output (each knows its baseEntries)
 * @param {Array} fresh - entries just loaded from BQE
 * @returns {Object} { keep, dropped }
 */
export function sortEditsForRefresh(edits, fresh) {
	const now = groupEntries(fresh);
	const keep = [];
	const dropped = [];
	edits.forEach((c) => {
		const group =
			now.get(groupKey(c.projectId, c.activityId, c.date)) || [];
		const untouched =
			sameGroup(c.baseEntries, group) && !group.some((e) => e.lockReason);
		(untouched ? keep : dropped).push(c);
	});
	return { keep, dropped };
}

/**
 * What's saved as the week's draft: the unsent changes, lines added without
 * hours yet, and projects added with no lines
 */
export function draftFor(wk, days) {
	return {
		cells: changedCells(wk.rows, wk.baseline, days),
		lines: wk.rows
			.filter(
				(r) =>
					!r.cells.some((c) => toNum(c.hours) > 0 || c.memo.trim()),
			)
			.filter(
				(r) =>
					!wk.baseline.some(
						(e) =>
							e.projectId === r.projectId &&
							e.activityId === r.activityId,
					),
			)
			.map((r) => ({
				projectId: r.projectId,
				activityId: r.activityId,
				days: r.days || [],
			})),
		keptProjectIds: wk.keptProjectIds,
	};
}

// JSON with object keys sorted, so a draft compares equal however it was
// stored (MySQL's JSON column re-orders keys)
export const stableJson = (value) =>
	JSON.stringify(value, (_, v) =>
		v && typeof v === "object" && !Array.isArray(v)
			? Object.fromEntries(
					Object.keys(v)
						.sort()
						.map((k) => [k, v[k]]),
				)
			: v,
	);

export const isEmptyDraft = (d) =>
	!d || (!d.cells.length && !d.lines.length && !d.keptProjectIds.length);

/**
 * The status chip for a week or day: "Not sent to BQE" until they're sent to
 * BQE; otherwise it reflects the BQE entries (Approved / Submitted once BQE has
 * locked all of them, Saved while they can still be changed). null (no chip)
 * when there's no time and nothing unsent.
 */
export function postStatus(changes, baseline, onlyDate) {
	if (changes.length) return "Not sent to BQE";
	const entries = onlyDate
		? baseline.filter((e) => e.date === onlyDate)
		: baseline;
	if (!entries.length) return null;
	if (entries.every((e) => e.lockReason === "Approved")) return "Approved";
	if (entries.every((e) => e.lockReason)) return "Submitted";
	return "Saved";
}

/**
 * Where a cell's time stands, for its color: kept in Tools and not sent yet
 * ("unsent", or "removed" when hours BQE has were cleared), in BQE and still
 * editable ("sent"), or locked in BQE ("submitted" / "approved" / "billed").
 * null for an empty cell with nothing to send.
 */
export const CELL_STATES = {
	unsent: {
		label: "Not sent to BQE",
		tip: "Kept in Tools as a draft. Send to BQE to save it there.",
	},
	// Shown instead of "unsent" when the week's draft failed to save
	unsaved: {
		label: "Draft not saved",
		tip: "Not sent to BQE, and the draft didn't save to Tools.",
	},
	removed: {
		label: "Removed, not sent",
		tip: "Cleared here. Send to BQE to delete it there.",
	},
	sent: { label: "In BQE", tip: "Saved in BQE." },
	submitted: { label: "Submitted", tip: "Submitted for approval in BQE." },
	approved: { label: "Approved", tip: "Approved in BQE." },
	billed: { label: "Billed", tip: "Billed in BQE." },
};

// Lookup keys for changedCells() output, to tell which cells are unsent
export const changedKeySet = (changes) =>
	new Set(changes.map((c) => groupKey(c.projectId, c.activityId, c.date)));

export function cellState(cell, projectId, activityId, date, changedKeys) {
	if (cell.lockReason === "Approved") return "approved";
	if (cell.lockReason === "Submitted") return "submitted";
	if (cell.lockReason) return "billed";
	const has = toNum(cell.hours) > 0;
	if (changedKeys.has(groupKey(projectId, activityId, date)))
		return has ? "unsent" : "removed";
	// Unchanged hours match what BQE has
	return has ? "sent" : null;
}

// Loads and holds everything except the week's rows (TimeEntry owns those)
export function useTimeEntryDataStore(instance, account) {
	const [employee, setEmployee] = useState(null);
	const [projects, setProjects] = useState([]);
	const [projectsState, setProjectsState] = useState("loading"); // loading | ready | error
	const [entryProjects, setEntryProjects] = useState({}); // projects only known from entries
	const [activitiesById, setActivitiesById] = useState({});
	const [activityLists, setActivityLists] = useState({}); // projectId -> ids | "loading" | "error"
	const inFlight = useRef(new Map()); // projectId -> promise of its activities
	const basicsLoadedAt = useRef(0);

	// silent: refresh in the background, keeping the current list on screen
	const loadBasics = useCallback(
		async (silent = false) => {
			if (!silent) setProjectsState("loading");
			try {
				const [me, list] = await Promise.all([
					getAPICall(instance, account, "/api/time-entry/me"),
					getAPICall(instance, account, "/api/time-entry/projects"),
				]);
				setEmployee(me.data);
				setProjects(list.data.projects || []);
				setProjectsState("ready");
				basicsLoadedAt.current = Date.now();
			} catch (error) {
				console.error("TimeEntry - loadBasics - error:", error);
				if (!silent)
					setProjectsState(signInExpired(error) ? "auth" : "error");
			}
		},
		[instance, account],
	);

	const refreshBasicsIfStale = useCallback(() => {
		if (
			basicsLoadedAt.current &&
			Date.now() - basicsLoadedAt.current >= BASICS_STALE_MS
		) {
			loadBasics(true);
		}
	}, [loadBasics]);

	const projectsById = useMemo(() => {
		const map = { ...entryProjects };
		projects.forEach((p) => (map[p.id] = p));
		return map;
	}, [projects, entryProjects]);

	const projectOf = useCallback(
		(id) =>
			projectsById[id] || {
				id,
				name: "Unknown project",
				number: "",
				client: "",
				overhead: false,
				mostUsedActivityId: null,
			},
		[projectsById],
	);

	const activityOf = useCallback(
		(id) =>
			activitiesById[id] || {
				id,
				name: "Activity",
				group: "",
				description: "",
				billable: true,
			},
		[activitiesById],
	);

	// Remember names from a week's entries (phases no longer active, activities
	// not loaded yet) so rows always have something to show
	const registerEntries = useCallback((entries) => {
		setActivitiesById((prev) => {
			const next = { ...prev };
			entries.forEach((e) => {
				if (!next[e.activityId]) {
					next[e.activityId] = {
						id: e.activityId,
						name: e.activityName || "Activity",
						group: "",
						description: e.activityName || "",
						billable: e.billable,
					};
				}
			});
			return next;
		});
		setEntryProjects((prev) => {
			const next = { ...prev };
			entries.forEach((e) => {
				if (!next[e.projectId]) {
					next[e.projectId] = {
						id: e.projectId,
						name: e.projectName || "Unknown project",
						number: "",
						client: "",
						overhead: false,
						mostUsedActivityId: null,
					};
				}
			});
			return next;
		});
	}, []);

	const fetchActivities = useCallback(
		async (projectId) => {
			setActivityLists((prev) => ({ ...prev, [projectId]: "loading" }));
			try {
				const res = await getAPICall(
					instance,
					account,
					"/api/time-entry/activities",
					{ projectId },
				);
				const list = res.data.activities || [];
				setActivitiesById((prev) => {
					const next = { ...prev };
					list.forEach((a) => (next[a.id] = a));
					return next;
				});
				setActivityLists((prev) => ({
					...prev,
					[projectId]: list.map((a) => a.id),
				}));
				return list;
			} catch (error) {
				console.error("TimeEntry - ensureActivities - error:", error);
				inFlight.current.delete(projectId); // allow a retry
				setActivityLists((prev) => ({ ...prev, [projectId]: "error" }));
				return [];
			}
		},
		[instance, account],
	);

	// Fetches a project's allowed activities once and resolves to the list
	// (later calls share the same request); [] if it failed
	const ensureActivities = useCallback(
		(projectId) => {
			if (!projectId) return Promise.resolve([]);
			if (!inFlight.current.has(projectId)) {
				inFlight.current.set(projectId, fetchActivities(projectId));
			}
			return inFlight.current.get(projectId);
		},
		[fetchActivities],
	);

	// "loading" | "error" | "ready" | undefined (not requested yet)
	const activitiesState = (projectId) => {
		const v = activityLists[projectId];
		return Array.isArray(v) ? "ready" : v;
	};

	// Activities allowed on a project, its most-used one first
	const activitiesFor = (projectId, excludeIds = []) => {
		const ids = activityLists[projectId];
		if (!Array.isArray(ids)) return [];
		const most = projectOf(projectId).mostUsedActivityId;
		return ids
			.filter((id) => !excludeIds.includes(id))
			.sort((a, b) => (b === most) - (a === most))
			.map(activityOf);
	};

	// Most recently used first, then projects they're budgeted on / manage
	const recentProjectIds = useMemo(() => {
		const recent = projects
			.filter((p) => p.lastUsed)
			.sort((a, b) => (a.lastUsed < b.lastUsed ? 1 : -1))
			.map((p) => p.id);
		const yours = projects
			.filter((p) => p.yours && !p.lastUsed)
			.map((p) => p.id);
		return [...recent, ...yours].slice(0, RECENT_LIMIT);
	}, [projects]);

	// New entries follow the activity's BQE billable default; overhead never bills
	const billableDefault = (projectId, activityId) =>
		!projectOf(projectId).overhead &&
		activityOf(activityId).billable !== false;

	return {
		employee,
		projects,
		projectsState,
		loadBasics,
		refreshBasicsIfStale,
		projectOf,
		activityOf,
		registerEntries,
		ensureActivities,
		activitiesState,
		activitiesFor,
		recentProjectIds,
		billableDefault,
	};
}
